import type { EstateAvailability } from 'realitiq-utils/src/flatfinder';
import i18next from 'i18next';
import { env } from '@/constants/env';

export type FlatfinderLanguage = 'de' | 'en';

export function getFlatfinderImageUrl(
  mediaId: string,
  publicId?: string | null,
  authenticatedProjectId?: string | null
) {
  if (authenticatedProjectId) {
    return `${env.API_URL}/projects/${encodeURIComponent(authenticatedProjectId)}/flatfinder/media/${encodeURIComponent(mediaId)}`;
  }

  if (publicId) {
    return `${env.API_URL}/flatfinders/${encodeURIComponent(publicId)}/media/${encodeURIComponent(mediaId)}`;
  }

  return `${env.API_URL}/images/${encodeURIComponent(mediaId)}`;
}

export function getFlatfinderLanguage(): FlatfinderLanguage {
  const language =
    i18next.resolvedLanguage ||
    i18next.language ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en');
  return language.toLowerCase().startsWith('de') ? 'de' : 'en';
}

export function getFlatfinderAvailabilityLabel(
  availability: EstateAvailability,
  language: FlatfinderLanguage
) {
  const keys = {
    available: 'availabilityAvailable',
    reserved: 'availabilityReserved',
    sold: 'availabilitySold',
    rented: 'availabilityRented',
  } as const;
  return i18next.t(availability ? keys[availability] : 'availabilityNotSpecified', {
    lng: language,
  });
}

export function getFlatfinderAvailabilityOptions(
  language: FlatfinderLanguage,
  marketTypes: string[]
) {
  const statuses: Exclude<EstateAvailability, null>[] = ['available', 'reserved'];
  if (marketTypes.includes('buy')) statuses.push('sold');
  if (marketTypes.some((type) => type === 'rent' || type === 'lease')) statuses.push('rented');
  return statuses.map((status) => ({
    id: status,
    title: getFlatfinderAvailabilityLabel(status, language),
  }));
}

export function getFlatfinderAvailabilityColors(availability: EstateAvailability) {
  switch (availability) {
    case 'available':
      return { fill: '#16a34a', badge: 'bg-emerald-100 text-emerald-800' };
    case 'reserved':
      return { fill: '#d97706', badge: 'bg-amber-100 text-amber-800' };
    case 'sold':
    case 'rented':
      return { fill: '#dc2626', badge: 'bg-red-100 text-red-800' };
    default:
      return { fill: '#64748b', badge: 'bg-slate-200 text-slate-700' };
  }
}

export function getSafeFlatfinderExternalUrl(value: string | null | undefined) {
  if (!value) return null;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function getSafeFlatfinderContactUrl(value: string | null | undefined) {
  if (!value) return null;

  try {
    const url = new URL(value);
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

export function formatFlatfinderNumber(value: number, language: FlatfinderLanguage) {
  return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatFlatfinderPrice(
  value: number,
  currency: string,
  language: FlatfinderLanguage
) {
  try {
    return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${formatFlatfinderNumber(value, language)} ${currency}`;
  }
}
